import { useState, useCallback } from 'react';
import { Search, X, Filter, ChevronDown } from 'lucide-react';

// Filter types
export type FilterType = 'text' | 'select' | 'multiselect' | 'date' | 'daterange';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  id: string;
  label: string;
  type: FilterType;
  placeholder?: string;
  options?: FilterOption[]; // For select/multiselect
}

export interface FilterValue {
  [key: string]: string | string[] | { from?: string; to?: string } | undefined;
}

export interface TableFiltersProps {
  filters: FilterConfig[];
  values: FilterValue;
  onChange: (values: FilterValue) => void;
  onSearch?: (query: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
  className?: string;
}

export function TableFilters({
  filters,
  values,
  onChange,
  onSearch,
  searchValue = '',
  searchPlaceholder = 'Search...',
  showSearch = true,
  className = '',
}: TableFiltersProps) {
  const [localSearch, setLocalSearch] = useState(searchValue);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Handle search input
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearch(value);
      onSearch?.(value);
    },
    [onSearch]
  );

  // Clear search
  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    onSearch?.('');
  }, [onSearch]);

  // Handle filter change
  const handleFilterChange = useCallback(
    (filterId: string, value: FilterValue[string]) => {
      onChange({ ...values, [filterId]: value });
    },
    [values, onChange]
  );

  // Clear single filter
  const handleClearFilter = useCallback(
    (filterId: string) => {
      const newValues = { ...values };
      delete newValues[filterId];
      onChange(newValues);
    },
    [values, onChange]
  );

  // Clear all filters
  const handleClearAll = useCallback(() => {
    onChange({});
    setLocalSearch('');
    onSearch?.('');
  }, [onChange, onSearch]);

  // Check if any filters are active
  const hasActiveFilters = Object.keys(values).some((key) => {
    const value = values[key];
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return value.from || value.to;
    return !!value;
  });

  // Render filter input based on type
  const renderFilterInput = (filter: FilterConfig) => {
    const value = values[filter.id];

    switch (filter.type) {
      case 'text':
        return (
          <input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            placeholder={filter.placeholder || filter.label}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        );

      case 'select': {
        const selectValue = value as string | undefined;
        return (
          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === filter.id ? null : filter.id)}
              className="w-full flex items-center justify-between bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <span className={selectValue ? 'text-slate-300' : 'text-slate-500'}>
                {selectValue
                  ? filter.options?.find((o) => o.value === selectValue)?.label || selectValue
                  : filter.placeholder || `Select ${filter.label}`}
              </span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
            {openDropdown === filter.id && (
              <div className="absolute z-20 mt-1 w-full bg-slate-800 border border-slate-700 rounded shadow-lg max-h-60 overflow-auto">
                <button
                  onClick={() => {
                    handleClearFilter(filter.id);
                    setOpenDropdown(null);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-700"
                >
                  Clear selection
                </button>
                {filter.options?.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      handleFilterChange(filter.id, option.value);
                      setOpenDropdown(null);
                    }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-700 ${
                      selectValue === option.value ? 'text-cyan-400 bg-slate-700/50' : 'text-slate-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'multiselect': {
        const selectedValues = (value as string[]) || [];
        return (
          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === filter.id ? null : filter.id)}
              className="w-full flex items-center justify-between bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            >
              <span className={selectedValues.length > 0 ? 'text-slate-300' : 'text-slate-500'}>
                {selectedValues.length > 0
                  ? `${selectedValues.length} selected`
                  : filter.placeholder || `Select ${filter.label}`}
              </span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
            {openDropdown === filter.id && (
              <div className="absolute z-20 mt-1 w-full bg-slate-800 border border-slate-700 rounded shadow-lg max-h-60 overflow-auto">
                {selectedValues.length > 0 && (
                  <button
                    onClick={() => handleFilterChange(filter.id, [])}
                    className="w-full px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-700 border-b border-slate-700"
                  >
                    Clear all
                  </button>
                )}
                {filter.options?.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        const newValues = isSelected
                          ? selectedValues.filter((v) => v !== option.value)
                          : [...selectedValues, option.value];
                        handleFilterChange(filter.id, newValues);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-700 flex items-center gap-2 ${
                        isSelected ? 'text-cyan-400' : 'text-slate-300'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center ${
                          isSelected ? 'bg-cyan-600 border-cyan-600' : 'border-slate-600'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case 'date':
        return (
          <input
            type="date"
            value={(value as string) || ''}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
          />
        );

      case 'daterange': {
        const dateRange = (value as { from?: string; to?: string }) || {};
        return (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange.from || ''}
              onChange={(e) =>
                handleFilterChange(filter.id, { ...dateRange, from: e.target.value })
              }
              placeholder="From"
              className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            />
            <span className="text-slate-500">to</span>
            <input
              type="date"
              value={dateRange.to || ''}
              onChange={(e) =>
                handleFilterChange(filter.id, { ...dateRange, to: e.target.value })
              }
              placeholder="To"
              className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search and filter row */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search input */}
        {showSearch && (
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              type="text"
              value={localSearch}
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-10 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            />
            {localSearch && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}

        {/* Filter icon with active indicator */}
        {filters.length > 0 && (
          <div className="flex items-center gap-2">
            <Filter className={`h-4 w-4 ${hasActiveFilters ? 'text-cyan-400' : 'text-slate-500'}`} />
            {hasActiveFilters && (
              <button
                onClick={handleClearAll}
                className="text-sm text-cyan-400 hover:text-cyan-300"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {/* Filter inputs */}
      {filters.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filters.map((filter) => (
            <div key={filter.id}>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">
                {filter.label}
              </label>
              {renderFilterInput(filter)}
            </div>
          ))}
        </div>
      )}

      {/* Active filter tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(values).map(([key, value]) => {
            if (!value) return null;

            const filter = filters.find((f) => f.id === key);
            if (!filter) return null;

            let displayValue: string;
            if (Array.isArray(value)) {
              if (value.length === 0) return null;
              displayValue = `${value.length} selected`;
            } else if (typeof value === 'object') {
              if (!value.from && !value.to) return null;
              displayValue = `${value.from || '...'} - ${value.to || '...'}`;
            } else {
              if (filter.type === 'select') {
                displayValue = filter.options?.find((o) => o.value === value)?.label || value;
              } else {
                displayValue = value;
              }
            }

            return (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-800 rounded-full text-xs text-slate-300"
              >
                <span className="text-slate-500">{filter.label}:</span>
                {displayValue}
                <button
                  onClick={() => handleClearFilter(key)}
                  className="text-slate-500 hover:text-slate-300"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Click outside handler for dropdowns */}
      {openDropdown && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />
      )}
    </div>
  );
}

// Quick search component for simple search-only scenarios
export interface QuickSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function QuickSearch({
  value,
  onChange,
  placeholder = 'Search...',
  className = '',
}: QuickSearchProps) {
  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-10 pr-10 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
      />
      {value && (
        <button
          onClick={() => onChange('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
